import React, { useEffect, useState } from 'react';
import { Table, Input, Avatar, Tag, Drawer, Descriptions, Space, Typography, Button, Popconfirm, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import request from '../utils/request';

const { Text } = Typography;

// 挂号状态文案
const REG_STATUS_MAP: Record<string, { text: string; color: string }> = {
  PENDING: { text: '排队中', color: 'orange' },
  CALLING: { text: '就诊中', color: 'blue' },
  COMPLETED: { text: '已完成', color: 'green' },
  CANCELLED: { text: '已取消', color: 'default' },
  ABSENT: { text: '已过号', color: 'red' },
  EXPIRED: { text: '已过期', color: 'default' },
};

const fmtDate = (v?: string) => (v ? String(v).slice(0, 10) : '-');
const fmtTime = (v?: string) => (v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-');

const Users: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchList = async (p = page, kw = keyword) => {
    setLoading(true);
    try {
      const res: any = await request.get('/user', {
        params: { page: p, pageSize: 10, keyword: kw || undefined },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  const openDetail = async (id: number) => {
    const res: any = await request.get(`/user/${id}`);
    setDetail(res);
    setDetailOpen(true);
  };

  // 封禁/解封：封禁后无法登录、无法挂号与下单
  const toggleBan = async (record: any) => {
    const next = !record.banned;
    await request.put(`/user/${record.id}/ban`, { banned: next });
    message.success(next ? '已封禁该用户' : '已解除封禁');
    fetchList();
    if (detail?.id === record.id) setDetail({ ...detail, banned: next });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '头像', dataIndex: 'avatar', width: 70,
      render: (v: string) => <Avatar src={v || undefined} icon={<UserOutlined />} />,
    },
    { title: '昵称', dataIndex: 'nickname', render: (v: string) => v || '-' },
    { title: '姓名', dataIndex: 'realName', width: 100, render: (v: string) => v || '-' },
    { title: '手机号', dataIndex: 'phone', width: 130, render: (v: string) => v || '-' },
    {
      title: '积分', width: 80,
      render: (_: any, r: any) => r.pointsAccount?.total ?? 0,
    },
    {
      title: '挂号', width: 70,
      render: (_: any, r: any) => r._count?.registrations ?? 0,
    },
    { title: '订单', dataIndex: 'orderCount', width: 70 },
    {
      title: '协议', width: 90, dataIndex: 'agreementAgreedAt',
      render: (v: string) => v ? <Tag color="success">已签署</Tag> : <Tag>未签署</Tag>,
    },
    {
      title: '状态', width: 90, dataIndex: 'banned',
      render: (v: boolean) => v ? <Tag color="red">已封禁</Tag> : <Tag color="green">正常</Tag>,
    },
    {
      title: '注册时间', dataIndex: 'createdAt', width: 160,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '操作', width: 100, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title={r.banned ? '确认解除该用户封禁？' : '封禁后该用户无法登录、挂号、下单，确认封禁？'}
            onConfirm={() => toggleBan(r)}
          >
            <Button size="small" danger={!r.banned}>{r.banned ? '解封' : '封禁'}</Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>用户管理</h2>
        <Input.Search
          placeholder="搜索昵称 / 姓名 / 手机号"
          allowClear
          style={{ width: 280 }}
          onSearch={(v) => { setKeyword(v); setPage(1); fetchList(1, v); }}
        />
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1200 }}
        onRow={(record) => ({ onClick: () => openDetail(record.id), style: { cursor: 'pointer' } })}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Drawer title="用户详情" open={detailOpen} onClose={() => setDetailOpen(false)} width={520}>
        {detail && (
          <>
            <Space align="center" style={{ marginBottom: 16 }}>
              <Avatar size={56} src={detail.avatar || undefined} icon={<UserOutlined />} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{detail.nickname || '-'}</div>
                <Text type="secondary">ID：{detail.id}</Text>
              </div>
            </Space>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="姓名">{detail.realName || '-'}</Descriptions.Item>
              <Descriptions.Item label="身份证号">{detail.idCard || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{detail.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="住址">{detail.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户协议">
                {detail.agreementAgreedAt ? `已签署（${fmtTime(detail.agreementAgreedAt)}）` : '未签署'}
              </Descriptions.Item>
              <Descriptions.Item label="账号状态">
                {detail.banned ? <Tag color="red">已封禁</Tag> : <Tag color="green">正常</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="当前积分">{detail.pointsAccount?.total ?? 0}</Descriptions.Item>
              <Descriptions.Item label="挂号次数">{detail._count?.registrations ?? 0}</Descriptions.Item>
              <Descriptions.Item label="订单次数">{detail.orderCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label="反馈次数">{detail._count?.feedbacks ?? 0}</Descriptions.Item>
              <Descriptions.Item label="注册时间">{fmtTime(detail.createdAt)}</Descriptions.Item>
            </Descriptions>

            <h4 style={{ margin: '20px 0 8px' }}>最近挂号</h4>
            {detail.registrations?.length ? (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={detail.registrations}
                columns={[
                  { title: '日期', dataIndex: 'regDate', width: 100, render: (v: string) => fmtDate(v) },
                  { title: '号码', dataIndex: 'queueNo', width: 60, render: (v: number) => `${v}号` },
                  { title: '就诊人', dataIndex: 'patientName' },
                  {
                    title: '状态', dataIndex: 'status', width: 90,
                    render: (v: string) => {
                      const s = REG_STATUS_MAP[v] || { text: v, color: 'default' };
                      return <Tag color={s.color}>{s.text}</Tag>;
                    },
                  },
                ]}
              />
            ) : (
              <Text type="secondary">暂无挂号记录</Text>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Users;
